import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, Play, Clock, User } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";

interface VideoTutorial {
  id: string;
  title: string;
  description: string;
  youtube_url: string;
  duration: string;
  category: string;
  created_at: string;
  is_active: boolean;
}

const VideoTutorials = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [videos, setVideos] = useState<VideoTutorial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const { data, error } = await supabase
        .from('video_tutorials')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVideos(data || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Dummy videos for demonstration
  const dummyVideos = [
    {
      id: '1',
      title: 'Getting Started with Bulk Image Generation',
      description: 'Learn the basics of generating multiple images at once',
      youtube_url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      duration: '5:30',
      category: 'Beginner',
      created_at: '2024-01-15',
      is_active: true
    },
    {
      id: '2',
      title: 'Advanced Prompting Techniques',
      description: 'Master the art of writing effective prompts',
      youtube_url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      duration: '8:45',
      category: 'Advanced',
      created_at: '2024-01-10',
      is_active: true
    }
  ];

  const displayVideos = videos.length > 0 ? videos : dummyVideos;
  
  const filteredVideos = displayVideos.filter(video =>
    video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    video.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getYouTubeId = (url: string) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  // Create video schema for rich results
  const videoListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'AI Image Generation Video Tutorials',
    description: 'Learn how to use The Bulk Image Generator with step-by-step video tutorials',
    itemListElement: displayVideos.slice(0, 10).map((video, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'VideoObject',
        name: video.title,
        description: video.description,
        thumbnailUrl: `https://img.youtube.com/vi/${getYouTubeId(video.youtube_url)}/maxresdefault.jpg`,
        uploadDate: video.created_at,
        duration: `PT${video.duration.replace(':', 'M')}S`,
        contentUrl: video.youtube_url,
        publisher: {
          '@type': 'Organization',
          name: 'The Bulk Image Generator',
        },
      },
    })),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SEO
        title="Video Tutorials | Learn AI Image Generation Step-by-Step"
        description="Watch step-by-step video tutorials on AI image generation. Learn prompt engineering, bulk generation, and advanced techniques from our expert guides."
        keywords="ai tutorials, image generation videos, prompt engineering tutorial, bulk generation guide, ai art tutorials, how to use ai images"
        url="/help/videos"
        jsonLd={videoListSchema}
      />
      <Header />
      
      <main className="pt-24">
        <div className="container mx-auto px-6 py-12">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-300 border-blue-500/30">
              Video Tutorials
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Video <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Tutorials</span>
            </h1>
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Search video tutorials..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 bg-white/10 border-white/20 text-white placeholder:text-gray-400 text-lg py-4"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center text-white">Loading videos...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredVideos.map((video) => {
                const youtubeId = getYouTubeId(video.youtube_url);
                return (
                  <Card key={video.id} className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all duration-300">
                    <CardHeader className="p-0">
                      <div className="relative aspect-video rounded-t-lg overflow-hidden">
                        {youtubeId ? (
                          <img
                            src={`https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`}
                            alt={video.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Try medium quality if max quality fails
                              const target = e.currentTarget;
                              if (target.src.includes('maxresdefault')) {
                                target.src = `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`;
                              } else if (target.src.includes('mqdefault')) {
                                target.src = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
                              } else {
                                // If all fail, show placeholder
                                target.style.display = 'none';
                                target.nextElementSibling?.classList.remove('hidden');
                              }
                            }}
                          />
                        ) : null}
                        <div className={`w-full h-full bg-gradient-to-br from-blue-600/20 to-purple-600/20 flex items-center justify-center ${youtubeId ? 'hidden' : ''}`}>
                          <Play className="w-12 h-12 text-white/60" />
                        </div>
                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                          <Play className="w-16 h-16 text-white" />
                        </div>
                        <Badge className="absolute top-2 right-2 bg-black/70 text-white">
                          {video.category}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      <CardTitle className="text-white mb-2">{video.title}</CardTitle>
                      <p className="text-gray-300 mb-4">{video.description}</p>
                      <div className="flex items-center justify-between text-sm text-gray-400">
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {video.duration}
                        </div>
                        <a
                          href={video.youtube_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          Watch Video
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VideoTutorials;
